import type { ReactNode } from 'react'
import { isValidElement } from 'react'
import { isEmpty } from 'lodash'
import SkeletonCell from '@/shared/components/common/Skeleton/SkeletonCell'
import Title from '../../Title'
import type { ParagraphProps } from '../../Paragraph'
import Paragraph from '../../Paragraph'
import { cx } from 'class-variance-authority'
import Icon from '../../Icon'
import { Info } from 'lucide-react'
import Tooltip from '../../Tooltip'
import { localizeDate } from '@/shared/utils/dateUtils'
import ClipboardButtonIcon from '../../ClipboardButton/ClipboardButtonIcon'
import { useLocale } from 'next-intl'

export type FieldType = 'date' | 'custom'

type BaseField = {
    label?: ReactNode
    tooltip?: string
    value: ReactNode
    dataTestId?: string
    color?: ParagraphProps['color']
    variant?: ParagraphProps['variant']
    canCopy?: boolean
    isLoading?: boolean
}

type DateField = BaseField & {
    type: 'date'
    hasHours?: boolean
}

type CustomField = BaseField & {
    type?: 'custom'
}

export type DetailBlockField = DateField | CustomField

export type DetailBlockProps = {
    title?: ReactNode
    count?: string | number
    action?: ReactNode
    fields: DetailBlockField[]
    isLoading?: boolean
    columns?: number
    footer?: ReactNode
    display?: 'horizontal' | 'vertical'
}

const formatFieldValue = (
    value: ReactNode,
    locale: string,
    type?: FieldType,
    color?: ParagraphProps['color'],
    variant?: ParagraphProps['variant'],
    canCopy?: boolean,
    hasHours?: boolean
): ReactNode => {
    if (type && !isEmpty(value)) {
        if (type === 'date' && (typeof value === 'string' || value instanceof Date)) {
            return (
                <Paragraph color={color} variant={variant}>
                    {localizeDate(value, {
                        locale,
                        dateFormatOptions: {
                            hasHours
                        }
                    })}
                </Paragraph>
            )
        }
        if (isValidElement(value)) {
            return value
        }
    }

    if (canCopy && typeof value === 'string') {
        return (
            <div className="flex items-center gap-3">
                <Paragraph className="truncate" color={color} variant={variant}>
                    {value}
                </Paragraph>
                <ClipboardButtonIcon textToCopy={value} />
            </div>
        )
    }

    return isValidElement(value) ? (
        value
    ) : (
        <Paragraph className="truncate" color={color} variant={variant}>
            {value ? value : '-'}
        </Paragraph>
    )
}

const DetailBlock = ({
    title,
    count,
    action,
    fields,
    isLoading = false,
    columns = 1,
    footer,
    display = 'horizontal'
}: DetailBlockProps) => {
    const locale = useLocale()
    const renderFooter = (): ReactNode => {
        if (!isEmpty(footer)) {
            return isValidElement(footer) ? (
                footer
            ) : (
                <Paragraph size={1} color="lightGrey" className="mt-4">
                    {footer}
                </Paragraph>
            )
        }
        return null
    }

    return (
        <div className="p-8 bg-backgroundPrimary border-0.5 border-borderPrimary rounded-3 shadow-1 w-full h-fit">
            {title ? (
                <div className={cx(['flex mb-5', action ? 'justify-between' : 'justify-start'])}>
                    <div className="flex items-center justify-start gap-2">
                        {isValidElement(title) ? title : <Title.Lg>{title ?? '-'} </Title.Lg>}
                        {count ? (
                            <div className="flex bg-backgroundDisabled px-1">
                                <Paragraph color="darkGrey" variant="medium">
                                    {count}
                                </Paragraph>
                            </div>
                        ) : null}
                    </div>
                    {action ? action : null}
                </div>
            ) : null}

            <div className={cx('flex flex-col gap-6', display === 'vertical' ? 'grid grid-cols-2' : undefined)}>
                {fields?.map((field, index) => (
                    <div
                        data-testid={field.dataTestId}
                        className={cx([
                            'grid gap-2 items-start',
                            columns === 1 ? 'grid-cols-1' : 'grid-cols-[40%_60%]'
                        ])}
                        // eslint-disable-next-line react/no-array-index-key -- a display-only list: its rows hold no state, and their values can repeat
                        key={index}
                    >
                        {field.label ? (
                            <div className="flex justify-start items-center gap-2">
                                <Paragraph size={1} color="darkGrey" className="truncate">
                                    {field.label}
                                </Paragraph>
                                {field.tooltip ? (
                                    <Tooltip
                                        trigger={
                                            <span data-testid="__detail-block-tooltip-icon__">
                                                <Icon icon={Info} size="1" strokeWidth={0.8} />
                                            </span>
                                        }
                                        content={field.tooltip}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                        {isLoading || field.isLoading ? (
                            <SkeletonCell />
                        ) : (
                            <div className="flex items-start justify-start w-full">
                                {formatFieldValue(
                                    field.value,
                                    locale,
                                    field?.type,
                                    field?.color,
                                    field?.variant,
                                    field?.canCopy,
                                    field.type === 'date' ? field.hasHours : undefined
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {renderFooter()}
        </div>
    )
}

export default DetailBlock
